import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPackage, FiDollarSign, FiShoppingBag, FiTrendingUp, FiLogOut, FiPlus, FiList, FiBox, FiCreditCard, FiEdit2, FiSettings, FiClock, FiShare2, FiCopy, FiTag, FiShield } from 'react-icons/fi';
import api from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrency } from '@/utils/formatters';
import OrderCard from '@/components/cards/OrderCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import PaymentConfigCard from '@/components/lojista/PaymentConfigCard';
import OperatingHoursCard from '@/components/lojista/OperatingHoursCard';
import StoreEarningsCard from '@/components/lojista/StoreEarningsCard';
import StoreQRCodeCard from '@/components/lojista/StoreQRCodeCard';
import FreightCoupons from '@/components/lojista/FreightCoupons';
import StoreFixedQR from '@/components/handshake/StoreFixedQR';

const LojistaDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('new');
  const [showPaymentConfig, setShowPaymentConfig] = useState(false);
  const [showOperatingHours, setShowOperatingHours] = useState(false);
  const [showEarnings, setShowEarnings] = useState(false);
  const [showCoupons, setShowCoupons] = useState(false);
  const [showHandshakeQR, setShowHandshakeQR] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      try {
        const storeResponse = await api.get('/stores/my');
        setStore(storeResponse.data);
        
        // Load share link
        try {
          const linkResponse = await api.get('/stores/my/share-link');
          setShareLink(linkResponse.data);
        } catch (e) {
          console.log('Could not load share link');
        }
        
        const [statsResponse, ordersResponse] = await Promise.all([
          api.get('/stats/store'),
          api.get('/orders')
        ]);
        
        setStats(statsResponse.data);
        setOrders(ordersResponse.data);
      } catch (error) {
        if (error.response?.status === 404) {
          setStore(null);
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyShareLink = () => {
    if (shareLink) {
      const fullUrl = `${window.location.origin}/loja/${shareLink.slug}`;
      navigator.clipboard.writeText(fullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareStore = async () => {
    if (shareLink && navigator.share) {
      try {
        await navigator.share({
          title: store?.name,
          text: `Confira os produtos da ${store?.name}!`,
          url: `${window.location.origin}/loja/${shareLink.slug}`
        });
      } catch (err) {
        copyShareLink();
      }
    } else {
      copyShareLink();
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { status: newStatus });
      loadData();
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error.response?.data?.detail || 'Erro ao atualizar status');
    }
  };

  const filteredOrders = orders.filter(order => {
    if (activeTab === 'new') return order.status === 'pendente';
    if (activeTab === 'preparing') return order.status === 'preparando';
    if (activeTab === 'ready') return order.status === 'coleta';
    if (activeTab === 'delivered') return order.status === 'entregue';
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50">
        <header className="bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <h1 className="text-xl font-bold">Painel do Lojista</h1>
            <button onClick={() => { logout(); navigate('/'); }} className="p-2 hover:bg-white/10 rounded-full" data-testid="lojista-logout-btn">
              <FiLogOut />
            </button>
          </div>
        </header>
        
        <main className="max-w-7xl mx-auto px-4 py-12">
          <div className="text-center">
            <FiShoppingBag className="mx-auto text-6xl text-gray-300 mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Crie sua Loja</h2>
            <p className="text-gray-500 mb-6">Você ainda não tem uma loja cadastrada</p>
            <button
              onClick={() => navigate('/lojista/loja/criar')}
              className="px-6 py-3 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] inline-flex items-center gap-2"
            >
              <FiPlus /> Criar Loja
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-gradient-to-r from-[#DC143C] to-[#8B0000] text-white">
        <div className="max-w-7xl mx-auto px-3 md:px-4 py-3 md:py-4">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div className="flex items-center gap-3">
              {/* Store Logo */}
              {store.logo ? (
                <img src={store.logo} alt={store.name} className="w-10 h-10 rounded-lg object-cover" />
              ) : (
                <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                  <span className="text-lg font-bold">{store.name[0]}</span>
                </div>
              )}
              <div>
                <h1 className="text-lg md:text-xl font-bold truncate max-w-[200px] md:max-w-none">{store.name}</h1>
                <p className="text-white/70 text-xs md:text-sm">Painel do Lojista</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => navigate('/lojista/loja/editar')} 
                className="p-2 hover:bg-white/10 rounded-full"
                title="Editar Loja"
              >
                <FiSettings className="text-lg md:text-xl" />
              </button>
              <button onClick={() => { logout(); navigate('/'); }} className="p-2 hover:bg-white/10 rounded-full" data-testid="lojista-logout-btn">
                <FiLogOut className="text-lg md:text-xl" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6 space-y-4 md:space-y-6">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <div className="bg-white rounded-xl p-3 md:p-4 shadow-sm">
              <div className="flex items-center gap-2 md:gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <FiPackage className="text-blue-600 text-sm md:text-base" />
                </div>
                <div>
                  <p className="text-xl md:text-2xl font-bold text-gray-800">{stats.total_orders}</p>
                  <p className="text-[10px] md:text-xs text-gray-500">Pedidos</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <FiDollarSign className="text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{formatCurrency(stats.total_revenue)}</p>
                  <p className="text-xs text-gray-500">Receita</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                  <FiTrendingUp className="text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.pending_orders}</p>
                  <p className="text-xs text-gray-500">Pendentes</p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                  <FiBox className="text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-800">{stats.products_count}</p>
                  <p className="text-xs text-gray-500">Produtos</p>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="flex gap-3">
          <button onClick={() => navigate('/lojista/produtos')} className="flex-1 py-3 px-4 bg-white rounded-xl shadow-sm flex items-center justify-center gap-2 text-gray-700 hover:bg-gray-50">
            <FiList /> Produtos
          </button>
          <button onClick={() => navigate('/lojista/produto/novo')} className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl shadow-sm flex items-center justify-center gap-2 hover:bg-blue-700">
            <FiPlus /> Novo Produto
          </button>
        </div>

        {/* Share Store Link Card */}
        {shareLink && (
          <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-xl p-4 text-white shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold flex items-center gap-2">
                  <FiShare2 /> Compartilhe sua Loja
                </h3>
                <p className="text-xs text-white/80 mt-1">Divulgue para seus clientes no WhatsApp, Instagram...</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={copyShareLink}
                  className="px-3 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <FiCopy className="text-xs" />
                  {copied ? 'Copiado!' : 'Copiar'}
                </button>
                <button
                  onClick={shareStore}
                  className="px-3 py-2 bg-white text-purple-600 hover:bg-gray-100 rounded-lg text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <FiShare2 className="text-xs" />
                  Enviar
                </button>
              </div>
            </div>
            <div className="mt-2 bg-white/10 rounded-lg px-3 py-2 text-sm font-mono truncate">
              {window.location.origin}/loja/{shareLink.slug}
            </div>
          </div>
        )}

        {/* Config Buttons */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Payment Config Button */}
          <button
            onClick={() => { setShowPaymentConfig(!showPaymentConfig); setShowOperatingHours(false); setShowEarnings(false); setShowCoupons(false); setShowHandshakeQR(false); }}
            className={`py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
              showPaymentConfig 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FiCreditCard />
            <span className="text-sm">Pagamentos</span>
          </button>

          {/* Operating Hours Button */}
          <button
            onClick={() => { setShowOperatingHours(!showOperatingHours); setShowPaymentConfig(false); setShowEarnings(false); setShowCoupons(false); setShowHandshakeQR(false); }}
            className={`py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
              showOperatingHours 
                ? 'bg-purple-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FiClock />
            <span className="text-sm">Horários</span>
          </button>

          {/* Earnings Button */}
          <button
            onClick={() => { setShowEarnings(!showEarnings); setShowPaymentConfig(false); setShowOperatingHours(false); setShowCoupons(false); setShowHandshakeQR(false); }}
            className={`py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
              showEarnings 
                ? 'bg-emerald-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
          >
            <FiTrendingUp />
            <span className="text-sm">Ganhos</span>
          </button>

          {/* Coupons Button */}
          <button
            onClick={() => { setShowCoupons(!showCoupons); setShowPaymentConfig(false); setShowOperatingHours(false); setShowEarnings(false); setShowHandshakeQR(false); }}
            className={`py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
              showCoupons 
                ? 'bg-orange-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="coupons-btn"
          >
            <FiTag />
            <span className="text-sm">Cupons</span>
          </button>

          {/* Handshake QR Button */}
          <button
            onClick={() => { setShowHandshakeQR(!showHandshakeQR); setShowPaymentConfig(false); setShowOperatingHours(false); setShowEarnings(false); setShowCoupons(false); }}
            className={`py-3 px-4 rounded-xl shadow-sm flex items-center justify-center gap-2 transition-colors ${
              showHandshakeQR 
                ? 'bg-green-600 text-white' 
                : 'bg-white text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="handshake-qr-btn"
          >
            <FiShield />
            <span className="text-sm">QR Segurança</span>
          </button>
        </div>

        {/* Config Panels */}
        {showPaymentConfig && (
          <PaymentConfigCard onConfigured={() => loadData()} />
        )}

        {showOperatingHours && (
          <OperatingHoursCard />
        )}

        {showEarnings && (
          <StoreEarningsCard />
        )}

        {showCoupons && (
          <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
            <FreightCoupons />
          </div>
        )}

        {showHandshakeQR && (
          <div className="mb-6">
            <StoreFixedQR />
          </div>
        )}

        {/* QR Code Card */}
        <div className="mb-6">
          <StoreQRCodeCard />
        </div>

        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="flex border-b overflow-x-auto">
            {[
              { key: 'new', label: 'Novos', count: orders.filter(o => o.status === 'pendente').length },
              { key: 'preparing', label: 'Preparando', count: orders.filter(o => o.status === 'preparando').length },
              { key: 'ready', label: 'Prontos', count: orders.filter(o => o.status === 'coleta').length },
              { key: 'delivered', label: 'Entregues', count: orders.filter(o => o.status === 'entregue').length }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex-1 min-w-[100px] py-3 px-4 text-sm font-medium transition-colors ${
                  activeTab === tab.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                {tab.count > 0 && <span className="ml-1 px-2 py-0.5 bg-gray-100 rounded-full text-xs">{tab.count}</span>}
              </button>
            ))}
          </div>

          <div className="p-4">
            {filteredOrders.length === 0 ? (
              <div className="text-center py-8">
                <FiPackage className="mx-auto text-4xl text-gray-300 mb-2" />
                <p className="text-gray-500">Nenhum pedido</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredOrders.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    onClick={() => {}}
                    showItemDetails={true}
                    actions={
                      <>
                        {order.status === 'pendente' && (
                          <button onClick={() => handleUpdateStatus(order.id, 'preparando')} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                            Confirmar Pedido
                          </button>
                        )}
                        {order.status === 'preparando' && (
                          <button onClick={() => handleUpdateStatus(order.id, 'coleta')} className="flex-1 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700">
                            Pronto para Coleta
                          </button>
                        )}
                      </>
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default LojistaDashboard;
